import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import type { FollowUp, FollowUpListQuery } from '@neuroedge/shared';
import { Button, Card, PageState, TextField, useToast } from '../../components/ui';
import { ApiClientError } from '../../api/client';
import { fieldErrorsFromApiError } from '../../lib/field-errors';
import { useAuth } from '../../auth/AuthContext';
import { AssigneeSelect } from '../../components/assignee/AssigneeSelect';
import {
  useFollowUps,
  useCreateFollowUp,
  useCompleteFollowUp,
  useUpdateFollowUp,
  useDeleteFollowUp,
} from '../../api/follow-ups';

interface Props {
  title?: string;
  query: FollowUpListQuery;
  attachTo?: { contactId?: string; leadId?: string };
  showTargets?: boolean;
}

/** `datetime-local` value (local time, no seconds) → ISO string. */
function toIso(local: string): string {
  return new Date(local).toISOString();
}

function statusBadge(f: FollowUp): { label: string; overdue: boolean } {
  if (f.status !== 'SCHEDULED') return { label: f.status, overdue: false };
  const overdue = new Date(f.dueAt).getTime() < Date.now();
  return { label: overdue ? 'OVERDUE' : 'SCHEDULED', overdue };
}

function FollowUpRow({
  followUp,
  canManage,
  showTargets,
  tenantUserId,
}: {
  followUp: FollowUp;
  canManage: boolean;
  showTargets?: boolean;
  tenantUserId: string | null;
}) {
  const complete = useCompleteFollowUp();
  const update = useUpdateFollowUp();
  const del = useDeleteFollowUp();
  const toast = useToast();
  const badge = statusBadge(followUp);
  const done = followUp.status === 'COMPLETED';
  const isMine = Boolean(tenantUserId) && followUp.assignedUser?.tenantUserId === tenantUserId;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 'var(--space-2)',
        padding: 'var(--space-2) 0',
        borderBottom: '1px solid var(--color-border)',
      }}
    >
      <span
        style={{
          flex: '1 1 220px',
          minWidth: 0,
          display: 'grid',
          gap: 2,
          textDecoration: done ? 'line-through' : undefined,
        }}
      >
        <span>{followUp.title ?? 'Follow up'}</span>
        {showTargets && (followUp.lead || followUp.contact) ? (
          <span
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 'var(--space-2)',
              fontSize: 'var(--font-size-sm)',
            }}
          >
            {followUp.lead ? (
              <Link to={`/leads/${followUp.lead.id}`}>{followUp.lead.title}</Link>
            ) : null}
            {followUp.contact ? (
              <Link to={`/contacts/${followUp.contact.id}`}>{followUp.contact.displayName}</Link>
            ) : null}
          </span>
        ) : null}
      </span>
      <span
        style={{
          fontSize: 'var(--font-size-sm)',
          whiteSpace: 'nowrap',
          color: badge.overdue ? 'var(--color-danger)' : 'var(--color-text-muted)',
        }}
      >
        {new Date(followUp.dueAt).toLocaleString()}
      </span>
      <span className="ne-badge">{badge.label}</span>
      {canManage ? (
        <AssigneeSelect
          compact
          label={`Assigned to (${followUp.title || 'follow-up'})`}
          value={followUp.assignedUser?.tenantUserId ?? ''}
          currentName={followUp.assignedUser?.displayName}
          disabled={update.isPending}
          onChange={(next) =>
            update.mutate(
              { id: followUp.id, patch: { assignedUserId: next || null } },
              {
                onSuccess: () =>
                  toast.success(
                    !next
                      ? 'Follow-up unassigned.'
                      : next === tenantUserId
                        ? 'Assigned to you.'
                        : 'Follow-up assigned.',
                  ),
                onError: (err) =>
                  toast.error(
                    err instanceof Error ? err.message : 'Could not assign the follow-up.',
                  ),
              },
            )
          }
        />
      ) : followUp.assignedUser ? (
        <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)' }}>
          {isMine ? 'Me' : followUp.assignedUser.displayName}
        </span>
      ) : null}
      {canManage && followUp.status === 'SCHEDULED' ? (
        <Button
          variant="secondary"
          disabled={complete.isPending}
          onClick={() =>
            complete.mutate(followUp.id, {
              onSuccess: () => toast.success('Follow-up completed.'),
              onError: (err) =>
                toast.error(
                  err instanceof Error ? err.message : 'Could not complete the follow-up.',
                ),
            })
          }
        >
          Done
        </Button>
      ) : null}
      {canManage ? (
        <Button
          variant="secondary"
          disabled={del.isPending}
          onClick={() =>
            del.mutate(followUp.id, {
              onSuccess: () => toast.success('Follow-up deleted.'),
              onError: (err) =>
                toast.error(err instanceof Error ? err.message : 'Could not delete the follow-up.'),
            })
          }
        >
          ✕
        </Button>
      ) : null}
    </div>
  );
}

export function FollowUpList({ title = 'Follow-ups', query, attachTo, showTargets }: Props) {
  const { hasPermission, tenantUserId } = useAuth();
  const canManage = hasPermission('tasks.manage');
  const followUps = useFollowUps({ pageSize: 200, sortBy: 'dueAt', sortOrder: 'asc', ...query });
  const create = useCreateFollowUp();
  const toast = useToast();
  const [newTitle, setNewTitle] = useState('');
  const [due, setDue] = useState('');
  /** Defaults to the person scheduling it. */
  const [assigneeId, setAssigneeId] = useState<string | null>(null);
  /** Until someone is picked, it's whoever is signed in (even if that resolves late). */
  const assignee = assigneeId ?? tenantUserId ?? '';
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  /** Mirrors the backend's `createFollowUpBody` limits (follow-ups.schemas.ts). */
  function validate(): Record<string, string> {
    const errors: Record<string, string> = {};
    const trimmedTitle = newTitle.trim();
    if (trimmedTitle.length > 200) {
      errors.title = 'Title must be 200 characters or fewer.';
    }
    if (!due) {
      errors.dueAt = 'Pick a date and time.';
    } else if (Number.isNaN(new Date(due).getTime())) {
      errors.dueAt = 'Enter a valid date and time.';
    }
    return errors;
  }

  async function onAdd(e: FormEvent) {
    e.preventDefault();
    const errors = validate();
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      setError('Please fix the highlighted fields.');
      return;
    }
    setError(null);
    try {
      await create.mutateAsync({
        dueAt: toIso(due),
        title: newTitle.trim() || undefined,
        assignedUserId: assignee || undefined,
        ...attachTo,
      });
      setNewTitle('');
      setDue('');
      setFieldErrors({});
      toast.success('Follow-up scheduled.');
    } catch (err) {
      if (err instanceof ApiClientError && err.code === 'VALIDATION_FAILED') {
        const serverFieldErrors = fieldErrorsFromApiError(err);
        if (Object.keys(serverFieldErrors).length > 0) {
          setFieldErrors(serverFieldErrors);
          setError('Please fix the highlighted fields.');
          toast.error('Please fix the highlighted fields.');
          return;
        }
      }
      const message = err instanceof Error ? err.message : 'Could not schedule the follow-up.';
      setError(message);
      toast.error(message);
    }
  }

  return (
    <Card title={title}>
      {canManage && attachTo ? (
        <form
          onSubmit={onAdd}
          style={{
            display: 'flex',
            gap: 'var(--space-2)',
            marginBottom: 'var(--space-3)',
            flexWrap: 'wrap',
            alignItems: 'flex-start',
          }}
          noValidate
        >
          <div style={{ flex: '1 1 160px' }}>
            <TextField
              label="Follow up about"
              placeholder="Follow up about…"
              value={newTitle}
              onChange={(e) => {
                setNewTitle(e.target.value);
                setFieldErrors((f) => {
                  if (!f.title) return f;
                  const { title: _removed, ...rest } = f;
                  return rest;
                });
              }}
              error={fieldErrors.title}
            />
          </div>
          <TextField
            label="Due"
            type="datetime-local"
            value={due}
            onChange={(e) => {
              setDue(e.target.value);
              setFieldErrors((f) => {
                if (!f.dueAt) return f;
                const { dueAt: _removed, ...rest } = f;
                return rest;
              });
            }}
            error={fieldErrors.dueAt}
          />
          <Button type="submit" disabled={create.isPending}>
            Schedule
          </Button>
          <div style={{ flex: '1 1 180px' }}>
            <AssigneeSelect value={assignee} onChange={setAssigneeId} />
          </div>
        </form>
      ) : null}
      {error ? (
        <p style={{ margin: '0 0 var(--space-2)', color: 'var(--color-danger)' }}>{error}</p>
      ) : null}

      {followUps.isPending ? (
        <PageState kind="loading" message="Loading follow-ups…" />
      ) : followUps.isError ? (
        <PageState
          kind="error"
          message={followUps.error.message}
          onRetry={() => void followUps.refetch()}
        />
      ) : followUps.data.items.length === 0 ? (
        <p style={{ color: 'var(--color-text-muted)', margin: 0 }}>No follow-ups.</p>
      ) : (
        <div>
          {followUps.data.items.map((f) => (
            <FollowUpRow
              key={f.id}
              followUp={f}
              canManage={canManage}
              showTargets={showTargets}
              tenantUserId={tenantUserId}
            />
          ))}
        </div>
      )}
    </Card>
  );
}
